<template>
    <div
        ref="splitRoot"
        class="mp-dock-split"
        :class="orientation === 'horizontal' ? 'mp-dock-split--horizontal' : 'mp-dock-split--vertical'"
        :data-node-id="node.id"
    >
        <template v-for="(child, index) in node.children" :key="child.id">
            <div
                class="mp-dock-split-pane"
                :style="paneStyle(index)"
            >
                <DockLayoutRenderer :node="child" />
            </div>
            <div
                v-if="index < node.children.length - 1"
                class="mp-dock-splitter"
                @pointerdown="(e) => onSplitterPointerDown(e, index)"
            />
        </template>
    </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { clampSplitPair, normalizeSizes } from './layoutTree.js';

export default {
    name: 'DockSplitNode',
    components: {
        DockLayoutRenderer: defineAsyncComponent(() =>
            import('./DockLayoutRenderer.vue'),
        ),
    },

    props: {
        node: { type: Object, required: true },
    },

    inject: {
        multipanelDockLayout: { default: null },
    },

    computed: {
        orientation() {
            return this.node.orientation || 'horizontal';
        },
    },

    beforeUnmount() {
        this.teardownSplitDrag();
    },

    methods: {
        paneStyle(index) {
            const sizes = normalizeSizes(this.node.sizes || []);
            const pct = (sizes[index] ?? 1 / this.node.children.length) * 100;
            const isHorizontal = this.orientation === 'horizontal';
            return isHorizontal
                ? { width: `${pct}%`, flex: '0 0 auto' }
                : { height: `${pct}%`, flex: '0 0 auto' };
        },

        onSplitterPointerDown(e, splitterIndex) {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();

            const root = this.$refs.splitRoot;
            if (!root) return;

            const target = e.currentTarget;
            const pointerId = e.pointerId;
            if (target?.setPointerCapture && pointerId != null) {
                try {
                    target.setPointerCapture(pointerId);
                } catch (_) {
                    /* ignore */
                }
            }

            const isHorizontal = this.orientation === 'horizontal';
            const rect = root.getBoundingClientRect();
            const total = isHorizontal ? rect.width : rect.height;
            if (total <= 0) return;

            const startPos = isHorizontal ? e.clientX : e.clientY;
            const startSizes = normalizeSizes([...this.node.sizes]);
            const i = splitterIndex;
            const j = splitterIndex + 1;

            const move = (ev) => {
                const pos = isHorizontal ? ev.clientX : ev.clientY;
                const delta = (pos - startPos) / total;
                let a = startSizes[i] + delta;
                let b = startSizes[j] - delta;
                [a, b] = clampSplitPair(a, b);
                const newSizes = [...startSizes];
                newSizes[i] = a;
                newSizes[j] = b;
                this.multipanelDockLayout?.updateSplitSizes?.(
                    this.node.id,
                    normalizeSizes(newSizes),
                );
            };

            const up = () => this.teardownSplitDrag();
            this._splitMove = { move, up, captureTarget: target, pointerId };
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
            window.addEventListener('pointercancel', up);
        },

        teardownSplitDrag() {
            if (!this._splitMove) return;
            const { move, up, captureTarget, pointerId } = this._splitMove;
            if (captureTarget?.releasePointerCapture && pointerId != null) {
                try {
                    if (captureTarget.hasPointerCapture?.(pointerId)) {
                        captureTarget.releasePointerCapture(pointerId);
                    }
                } catch (_) {
                    /* ignore */
                }
            }
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            window.removeEventListener('pointercancel', up);
            this._splitMove = null;
        },
    },
};
</script>
